import Axios from "helpers/Interceptor";

export interface MarketingPlanAudience {
    primaryPersona: {
        label: string;
        age: string;
        geographies: string[];
        interests: string[];
    };
}

export interface MarketingPlanBudget {
    total: number;
    tier?: 'low' | 'mid' | 'high';
    breakdown?: {
        paid_ads?: number;
        tools?: number;
        content_creation?: number;
        [key: string]: number | undefined;
    };
}

export interface MarketingPlanAction {
    id: string;
    name: string;
    startDate: string; // YYYY-MM-DD
    endDate: string;   // YYYY-MM-DD
    focus: string;
    title: string;
    description: string;
    platform: string;
    priority: 'high' | 'medium' | 'low';
    status?: 'pending' | 'in_progress' | 'completed' | 'failed';
    actionType: 'executable_ad' | 'on_platform_ad' | 'symphony_tool' | 'content_strategy' | 'external_strategy';
    expectedOutcomes?: string[];

    // Fields for executable_ad and on_platform_ad
    playbookType?: string;
    goalLink?: string;
    budget?: {
        amount: number;
        type: 'daily' | 'lifetime';
    };
    targetingSummary?: {
        audiences: string[];
        locations: string[];
    };
    creativeBrief?: string;
    conversionEventName?: string;

    // Fields for on_platform_ad
    campaignType?: 'video_views' | 'playlist_placement';

    // Fields for symphony_tool
    toolType?: 'website_builder' | 'presave_campaign' | 'link_in_bio';
    toolConfig?: {
        style?: 'music' | 'tour' | 'data_collection' | 'link_in_bio' | 'combined' | string;
        releaseDate?: string;
        platforms?: string[];
        collectEmails?: boolean;
        followArtist?: boolean;
        customBranding?: boolean;
        purpose?: string;
        includeAudioPlayer?: boolean;
        includeVideoEmbed?: boolean;
        includeSocialLinks?: boolean;
        includeEmailSignup?: boolean;
    };

    // Fields for content_strategy
    contentType?: 'video' | 'image' | 'text' | 'audio' | 'mixed';
    contentIdeas?: string[];
    executionNotes?: string;

    // Fields for external_strategy
    strategyType?: 'pr' | 'partnerships' | 'influencer' | 'radio' | 'other';
    strategyDescription?: string;
}

// Main Marketing Plan Interface
export interface MarketingPlan {
    id: string;
    brand_id: number;
    created_by_user_id?: number;
    name: string;
    status: 'draft' | 'active' | 'completed' | 'archived';
    plan_type?: 'launch' | 'evergreen' | 'single_release' | 'album_release';
    objective: string;
    target_outcomes: string[];
    audience: MarketingPlanAudience;
    budget: MarketingPlanBudget;
    actions: MarketingPlanAction[];
    createdAt?: string;
    updatedAt?: string;
}

// API Response Interfaces

// Response for getting all marketing plans for a brand
export interface GetMarketingPlansResponse {
    data: {
        brandId: string;
        plans: MarketingPlan[];
        total: number;
    };
    message: string;
    hasError: boolean;
}

// Response for getting a single marketing plan
export interface GetMarketingPlanResponse {
    data: MarketingPlan;
    message: string;
    hasError: boolean;
}

// Response for creating a marketing plan
export interface CreateMarketingPlanResponse {
    data: {
        brandId: number;
        planId: string;
        success: boolean;
        streamingEvent?: {
            type: string;
            payload: {
                workspaceType: string;
                data: {
                    planId: string;
                };
            };
        };
    };
    message: string;
    hasError: boolean;
}

// Response for updating a marketing plan
export interface UpdateMarketingPlanResponse {
    data: {
        brandId: string;
        planId: string;
        success: boolean;
    };
    message: string;
    hasError: boolean;
}

// Request interfaces for creating/updating plans
export interface CreateMarketingPlanRequest {
    name: string;
    plan_type?: 'launch' | 'evergreen' | 'single_release' | 'album_release';
    objective: string;
    target_outcomes: string[];
    audience: MarketingPlanAudience;
    budget: MarketingPlanBudget;
    status?: 'draft' | 'active' | 'completed' | 'archived';
}

export interface UpdateMarketingPlanRequest {
    name?: string;
    plan_type?: 'launch' | 'evergreen' | 'single_release' | 'album_release';
    objective?: string;
    target_outcomes?: string[];
    audience?: MarketingPlanAudience;
    budget?: MarketingPlanBudget;
    status?: 'draft' | 'active' | 'completed' | 'archived';
}

// Utility types for action type validation
export type ExecutableAdPlatforms = 'Meta' | 'TikTok';
export type OnPlatformAdPlatforms = 'YouTube' | 'Playlists';
export type SymphonyToolPlatforms = 'Website' | 'Presave' | 'LinkInBio';

// Helper interface for action validation
export interface ActionValidationRules {
    executable_ad: {
        platforms: ExecutableAdPlatforms[];
        requiredFields: ('playbookType' | 'goalLink' | 'budget' | 'targetingSummary')[];
    };
    on_platform_ad: {
        platforms: OnPlatformAdPlatforms[];
        requiredFields: ('campaignType' | 'goalLink' | 'budget' | 'targetingSummary')[];
    };
    symphony_tool: {
        platforms: SymphonyToolPlatforms[];
        requiredFields: ('toolType' | 'toolConfig')[];
    };
    content_strategy: {
        platforms: string[];
        requiredFields: ('contentType' | 'contentIdeas' | 'executionNotes')[];
    };
    external_strategy: {
        platforms: string[];
        requiredFields: ('strategyType' | 'strategyDescription')[];
    };
}


/**
 * Fetches a specific marketing plan by its ID
 * @param brandId - The ID of the brand
 * @param planId - The ID of the marketing plan to fetch
 * @returns Promise containing marketing plan details or null if not found/error
 */
export const getMarketingPlan_api = async (
    brandId: string | number,
    planId: string
): Promise<MarketingPlan | null> => {
    if (!brandId || !planId) {
        return null;
    }

    try {
        const response = await Axios.get<MarketingPlan>(`/marketing-plan/${brandId}/${planId}`);

        return response.data || null;
    } catch (error) {
        console.error('Failed to fetch marketing plan:', error);
        return null;
    }
}

/**
 * Fetches all marketing plans for a specific brand
 * @param brandId - The ID of the brand to fetch marketing plans for
 * @returns Promise containing array of marketing plans or empty array if error
 */
export const getMarketingPlansForBrand_api = async (
    brandId: string | number
): Promise<GetMarketingPlansResponse | null> => {
    if (!brandId) {
        return null;
    }

    try {
        const response = await Axios.get<GetMarketingPlansResponse>(`/marketing-plan/${brandId}`);

        return response.data || null;
    } catch (error) {
        console.error('Failed to fetch marketing plans for brand:', error);
        return null;
    }
}

/** Parameters for updating a marketing plan */
interface UpdateMarketingPlanProps {
    /** Brand ID */
    brandId: string | number;
    /** Marketing plan ID */
    planId: string;
    /** Updates to apply */
    updates: {
        budget?: {
            total: number;
            allocation: {
                ads: number;
                content: number;
                tools: number;
            };
        };
        audience?: any;
        timeline?: any;
        campaign?: any;
    };
}

/**
 * Updates an existing marketing plan
 * @param props - Parameters for updating the marketing plan
 * @returns Promise containing the updated marketing plan or throws error
 */
export const updateMarketingPlan_api = async ({
    brandId,
    planId,
    updates
}: UpdateMarketingPlanProps): Promise<any | null> => {
    try {
        const response = await Axios.put<{
            data: any;
            message: string;
            isError: boolean;
        }>(`/marketing-plan/${brandId}/${planId}`, updates);

        if (response.data?.isError) {
            throw new Error(response.data.message || 'Error updating marketing plan');
        }

        return response.data?.data || null;
    } catch (error) {
        console.error('Failed to update marketing plan:', error);
        throw error;
    }
}

/** Parameters for updating a marketing plan action status */
interface UpdateMarketingPlanActionStatusProps {
    /** Brand ID */
    brandId: string | number;
    /** Marketing plan ID */
    planId: string;
    /** Action ID */
    actionId: string;
    /** New status to set */
    status: 'pending' | 'in_progress' | 'completed';
}

/** Response for updating marketing plan action status */
interface UpdateMarketingPlanActionStatusResponse {
    brandId: string;
    planId: string;
    actionId: string;
    newStatus: string;
    updatedPlan: MarketingPlan;
    success: boolean;
}

/**
 * Updates the status of a marketing plan action
 * @param props - Parameters for updating the action status
 * @returns Promise containing the updated marketing plan or throws error
 */
export const updateMarketingPlanActionStatus_api = async ({
    brandId,
    planId,
    actionId,
    status
}: UpdateMarketingPlanActionStatusProps): Promise<MarketingPlan | null> => {
    try {
        const response = await Axios.patch<UpdateMarketingPlanActionStatusResponse>(
            `/marketing-plan/${brandId}/${planId}/actions/${actionId}/status`,
            { status }
        );

        return response.data?.updatedPlan || null;
    } catch (error) {
        console.error('Failed to update marketing plan action status:', error);
        throw error;
    }
}